import React, { useCallback } from "react";
import { Checkbox, Col, Row, Spin, Form, Tooltip } from "antd";
import { PopoverText } from "@/components";
import { useRequest } from "ahooks";
import { debounce } from "lodash";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import OrderExportField from "@/services/api/moreFeatures/orderExport/enum/Fields";
import FieldsNameMap from "@/services/api/moreFeatures/orderExport/map/FieldNameMap";
import { productFieldValueArray } from "../../util";
import useDragDrop, { useDragDropDataTransformer } from "@/hooks/useDragDrop";
import { useOrderExportStore } from "../../store";
import { sortOrderExportFields } from "./util";
import { savaOrderExportFieldsStatus } from "@/services/api/moreFeatures/orderExport";
import { observer } from "mobx-react-lite";

interface FieldConfig {
    value: OrderExportField;
    tip?: React.ReactNode;
    disableCheck?: (needExportingOrderItems: boolean) => boolean;
}

const fieldConfigs: FieldConfig[] = [
    {
        value: OrderExportField.AFFILIATE_MARKETER,
        tip: "仅抖店，快手和微信小店订单有带货达人，其他平台没有该信息，无需选择",
        disableCheck: (needExporting) =>
            !needExporting && productFieldValueArray.includes(OrderExportField.AFFILIATE_MARKETER),
    },
    {
        value: OrderExportField.SHIPPER_NAME,
        tip: "仅手工订单添加导入订单时选择了发货人信息或者订单列表设置了发货人信息才会导出",
    },
    {
        value: OrderExportField.ORDER_MARK,
        tip: "仅导出订单标记的标记备注，不导出标记图标",
    },
    {
        value: OrderExportField.SHIPPING_COST,
        tip: "买家支付的运费",
    },
    {
        value: OrderExportField.PRODUCT_TITLE,
        tip: "优先简称",
    },
];

interface FieldItemProps {
    field: FieldConfig;
    checked: boolean;
    needExportingOrderItems: boolean;
    index?: number;
    onDrag?: (from: number, to: number) => void;
    onDragEnd?: () => void;
}
const FieldItem: React.FC<FieldItemProps> = ({ field, checked, needExportingOrderItems, index, onDrag, onDragEnd }) => {
    const label = FieldsNameMap[field.value];
    const isDisabled = field.disableCheck?.(needExportingOrderItems) ?? false;
    const { ref, opacity } = useDragDrop({ index, onChange: onDrag, direction: "both", onChangeEnd: onDragEnd });
    const labelNode = field.tip ? (
        <PopoverText content={field.tip}>{label}</PopoverText>
    ) : (
        label
    );

    return (
        <Col className="order-export-field-item" key={field.value}>
            <span
                className={`order-export-field-content${index != null ? " is-draggable" : ""}`}
                style={{ opacity }}
                ref={index != null ? ref : undefined}
            >
                <Checkbox
                    className="order-export-field-checkbox"
                    value={field.value}
                    checked={checked}
                    disabled={isDisabled}
                >
                    {labelNode}
                </Checkbox>
            </span>
        </Col>
    );
};

interface ReadonlyOrderExportFieldListProps {
    value?: OrderExportField[];
    allFields: OrderExportField[];
    needExportingOrderItems: boolean;
}

export const ReadonlyOrderExportFieldList: React.FC<ReadonlyOrderExportFieldListProps> = ({
    value = [],
    allFields,
    needExportingOrderItems,
}) => {
    return (
        <div className="order-export-readonly-field-list">
            <Checkbox.Group style={{ width: "100%" }} value={value}>
                <Row className="order-export-field-list">
                    {allFields.map((fieldValue) => {
                        const field = fieldConfigs.find((item) => item.value === fieldValue) || {
                            value: fieldValue,
                            disableCheck: () => false,
                        };
                        const label = FieldsNameMap[field.value];
                        const isDisabled = field.disableCheck?.(needExportingOrderItems) ?? false;
                        const labelNode = field.tip ? (
                            <PopoverText content={field.tip}>{label}</PopoverText>
                        ) : (
                            label
                        );

                        return (
                            <Col className="order-export-field-item" key={fieldValue}>
                                <span className="order-export-field-content">
                                    <Checkbox
                                        className="order-export-field-checkbox"
                                        value={field.value}
                                        checked={value.includes(fieldValue)}
                                        disabled={isDisabled}
                                    >
                                        {labelNode}
                                    </Checkbox>
                                </span>
                            </Col>
                        );
                    })}
                </Row>
            </Checkbox.Group>
        </div>
    );
};

interface OrderExportFieldListProps {
    value?: OrderExportField[];
    needExportingOrderItems: boolean;
    editable?: boolean;
    onChange?: (value: OrderExportField[]) => void;
}
export const OrderExportFieldList: React.FC<OrderExportFieldListProps> = ({
    value = [],
    needExportingOrderItems,
    editable = true,
    onChange,
}) => {
    const store = useOrderExportStore();
    const { runAsync: saveRun, loading } = useRequest(savaOrderExportFieldsStatus, { manual: true });

    const debouncedSave = useCallback(
        debounce(
            async (fields: OrderExportField[]) => {
                const payload = store.orderExportFields.map((f) => ({ field: f, checked: fields.includes(f) }));
                await saveRun(payload);
            },
            750,
            { leading: false, trailing: true },
        ),
        [store.orderExportFields],
    );

    const handleChange = (fields: OrderExportField[]) => {
        const sorted = sortOrderExportFields(store.orderExportFields, fields);
        onChange?.(sorted);
        debouncedSave(fields);
    };

    const handleDragEnd = (newOrder: OrderExportField[]) => {
        store.updateOrderExportFields(newOrder);
        handleChange(value);
    };

    const { localList, onLocalChange, onLocalDragEnd } = useDragDropDataTransformer(
        store.orderExportFields,
        handleDragEnd,
    );

    // 非编辑模式下展示所有字段，已选项为 checked
    const listToRender = editable ? localList : store.orderExportFields;

    const container = (
        <Row className="order-export-field-list">
            {listToRender.map((fieldValue, idx) => {
                const cfg = fieldConfigs.find((c) => c.value === fieldValue) || {
                    value: fieldValue,
                    disableCheck: () => false,
                };
                const isChecked = value.includes(fieldValue);
                return (
                    <FieldItem
                        key={fieldValue}
                        field={cfg}
                        checked={isChecked}
                        needExportingOrderItems={needExportingOrderItems}
                        index={editable ? idx : undefined}
                        onDrag={editable ? onLocalChange : undefined}
                        onDragEnd={editable ? onLocalDragEnd : undefined}
                    />
                );
            })}
        </Row>
    );

    return (
        <div style={!editable ? { pointerEvents: "none", cursor: "not-allowed" } : null}>
            <Spin spinning={loading}>
                <Checkbox.Group style={{ width: "100%" }} value={value} onChange={handleChange}>
                    <DndProvider backend={HTML5Backend}>
                        <Tooltip title={editable ? "支持拖拽排序" : null} placement="right">
                            {container}
                        </Tooltip>
                    </DndProvider>
                </Checkbox.Group>
            </Spin>
        </div>
    );
};

interface FieldsFormItemProps {
    needExportingOrderItems: boolean;
}

export const FieldsFormItem: React.FC<FieldsFormItemProps> = ({ needExportingOrderItems }) => (
    <Form.Item
        name="fields"
        label="导出信息"
        messageVariables={{ label: "导出信息" }}
        required={false}
        rules={[{ required: true, message: "请至少选择一个导出信息" }]}
    >
        <OrderExportFieldList needExportingOrderItems={needExportingOrderItems} />
    </Form.Item>
);

export default observer(FieldsFormItem);
